import { AfterViewInit, Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DatePipe } from '@angular/common';
import { LocalStorageService } from 'src/app/shared/services/local-storage.service';
import { Constants } from 'src/app/shared/constants';
import { OrdersService } from 'src/app/feature/order/Service/order.service';
import { 
  Chart, 
  TimeScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import 'chartjs-plugin-trendline';
import 'chartjs-adapter-date-fns';
import { enUS } from 'date-fns/locale';

export const trendLinePlugin = {
  id: 'trendlinePlugin',
  afterDatasetsDraw: function (chart:any) {
    const showTrendline = chart.options.plugins?.trendlinePlugin?.showTrendline;
    if (showTrendline && chart.data.datasets[0].data.length > 1) {
      const ctx = chart.ctx;
      const dataset = chart.data.datasets[0].data.map((data) => data['y']) as number[];
      const labels = chart.data.datasets[0].data.map((data) => data['x']);      
      const xValues = labels.map(label => typeof label === 'number' ? label : new Date(label).getTime());
      const yValues = dataset;
      const n = xValues.length;
      const sumX = xValues.reduce((a, b) => a + b, 0);
      const sumY = yValues.reduce((a, b) => a + b, 0);
      const sumXY = xValues.reduce((sum, x, i) => sum + x * yValues[i], 0);
      const sumX2 = xValues.reduce((sum, x) => sum + x * x, 0);

      const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
      const intercept = (sumY - slope * sumX) / n;
      const trendlineStart = slope * xValues[0] + intercept;
      const trendlineEnd = slope * xValues[xValues.length - 1] + intercept;

      ctx.save();
      ctx.beginPath();
      ctx.moveTo(
        chart.scales['x'].getPixelForValue(xValues[0]), 
        chart.scales['y'].getPixelForValue(trendlineStart)
      );
      ctx.lineTo(
        chart.scales['x'].getPixelForValue(xValues[xValues.length - 1]),
        chart.scales['y'].getPixelForValue(trendlineEnd)
      );
      ctx.lineWidth = 2;
      ctx.setLineDash([2, 3]);
      ctx.strokeStyle = '#07548c';
      ctx.stroke();
      ctx.restore();
    }
}}

export const axisLabelPlugin={
  id: 'axisLabelPlugin',
  afterDraw: function (chart: any) {
      const ctx = chart.ctx;
      const yAxis = chart.scales['y'];
      const yAxisLabel = chart.options.scales?.y?.title?.text;
       if (yAxis && yAxisLabel) {
         ctx.save();
         ctx.font = 'bold 12px "Nunito Sans","Helvetica Neue Light","Helvetica Neue",Helvetica,Arial,"Lucida Grande",sans-serif';
         ctx.fillStyle = '#000';
         ctx.textAlign = 'left';
         ctx.textBaseline = 'bottom';
         // Position horizontally at top-left of chart area with spacing
         const xPosition = yAxis.left;
         const yPosition = yAxis.top - 15;
         ctx.fillText(yAxisLabel, xPosition, yPosition);
         ctx.restore();
       }  
}}

const DATE_FORMAT = 'MM/dd/yyyy';

@Component({
    selector: 'app-graph-dialog',
    templateUrl: './graph-dialog.component.html',
    styleUrls: ['./graph-dialog.component.scss'],
    providers: [DatePipe],
    standalone: false
})
export class GraphDialogComponent implements AfterViewInit , OnInit{
  @ViewChild('dialogHeader') dialogHeader: ElementRef;
  @ViewChild('dialogContent') dialogContent: ElementRef;
  @ViewChild(BaseChartDirective) resultHistoryChart: BaseChartDirective;
  @ViewChild('detailsContent', { static: false }) detailsContent: ElementRef;
  @ViewChild('graphContent', { static: false }) graphContent: ElementRef;
  @ViewChild('tableContent', { static: false }) tableContent: ElementRef;
  @ViewChild('info', { static: false }) info: ElementRef;

  isLoading = false;
  xTickAngle = 40;
  account: string;
  patient:string;
  patientNameOnly : string;
  testName:string;
  parameter:string;
  public resultUnit:string;
  results: Array<{collectionDate: Date, resultValueWithUnit: string}> = [];
  resultsToPlot: any[];
  dialogPlotData:{};
  disclaimerText: string;
  showTrendline = false;
  trendLinePlugin = trendLinePlugin;
  axisLabelPlugin = axisLabelPlugin;
  
  public lineChartData: any[] = [
    { 
      label: '',
      data:[], 
      lineTension:0,
      resultUnit: '',
      backgroundColor: 'transparent',
      borderColor: '#07548c',
      pointBackgroundColor: '#07548c', 
      pointBorderColor: '#07548c', 
      pointStyle: 'rect',
      pointHoverBorderColor: '#07548c',
     }
  ];
  
  public lineChartOptions: any = {
    responsive: true, 
    aspectRatio: 2.5,
    maintainAspectRatio: true,
    layout: {
      padding: {
        left: 10,
        top: 30,
        bottom: 10,
      },
    },
    plugins:{
      trendlinePlugin: {
        showTrendline: false
      },
      legend:{
        display: false, 
      },
      tooltip: {
        backgroundColor: '#07548c', 
        titleColor: '#ffffff', 
        bodyColor: '#ffffff',    
        padding: 8,
        displayColors: false,
        callbacks: {
          label: (context: any) => {
            const yValue = context.parsed.y;
            const unit = context.dataset.resultUnit || '';
            return `${yValue} ${unit}`;
          }
        }
      }
    },

    scales:{
      x:{
        type: 'time',
        time: {
          unit: 'day',
          displayFormats: {
            day: DATE_FORMAT,
          },
          tooltipFormat: DATE_FORMAT,
        },
        adapters: {
          date: {
            locale: enUS
          }
        },
        title:{
          display: true,
          text: 'Collection Date',
          color: 'black',
          font: {
            size: 12,
            weight: 'bold',
          family: "'Nunito Sans','Helvetica Neue Light','Helvetica Neue',Helvetica,Arial,'Lucida Grande',sans-serif",
          //padding: 10
        }
        },
        grid:{
          display:false
        },
        ticks:{
          autoSkip: true,
          maxTicksLimit: 10,
          color:'black',
          font: {
            size: 12,
          },
          minRotation: this.xTickAngle,
          maxRotation: this.xTickAngle,         
    }
        
      },      
      y:{
        title:{
          display: false,
          text: '',
          color: 'black',
          font: {
            size: 12,
            weight: 'bold',
          },
        },
        grid:{
          display:true,
          drawBorder: false
        },
        ticks:{
          beginAtZero:true, 
          color:'black',
          font: {
            size: 12,
          },
          min: 0, 
          maxTicksLimit:10
         } as any
      }
   }
  };
  public lineChartColors: any[] = [
    {
      backgroundColor: 'transparent',
      borderColor: '#07548c',
      pointBackgroundColor: '#07548c', 
      pointBorderColor: '#07548c', 
      pointStyle: "rect",
      pointHoverBorderColor: '#07548c', 
    }
  ];

  public lineChartLegend = false;
  public lineChartType = 'line';

  constructor(
    @Inject(MAT_DIALOG_DATA) public dialogData: any, 
    private readonly datePipe: DatePipe, 
    private readonly localstorageService: LocalStorageService,
    private readonly orderService: OrdersService, 
    public dialogRef: MatDialogRef<GraphDialogComponent>,     
) {
   
    Chart.register(
      TimeScale,
      LinearScale,
      PointElement,
      LineElement,
      Title,
      Tooltip,
      Legend,
      Filler
    );

    this.account = dialogData.mainAccountName ? `${dialogData.mainAccountName} (${dialogData.mainAccountCode})` : `(${dialogData.mainAccountCode})`;
    this.patient = dialogData.patientFirstName;
    this.patientNameOnly = dialogData.patientFirstName;
    if (dialogData.patientLastName) {
      this.patient = `${dialogData.patientLastName}, ${this.patient}`;
      this.patientNameOnly = `${dialogData.patientLastName} ${dialogData.patientFirstName}`;
    }
    if (dialogData.patientDOB) {
      this.patient += ` (${this.datePipe.transform(dialogData.patientDOB, DATE_FORMAT)})`;
    }
    this.testName = dialogData.testName;
    this.parameter = dialogData.resultUnit !== '' ? `${dialogData.parameterName} (${dialogData.resultUnit})` : `${dialogData.parameterName}`;
    this.resultUnit = dialogData.resultUnit;
    this.results = dialogData.results || [];
    this.resultsToPlot = this.results.map((result: any) => {
      if (typeof result === 'object' && result !== null) {
        return {
          ...result, 
          resultValueWithoutUnit: this.convertInputValueToPlot(result.resultValueWithUnit)
        };
      } else {
        return {}; 
      }
    });
  }

    onDialogOpen(): void {
      setTimeout(() => {
        if (this.resultHistoryChart) {
          this.resultHistoryChart.chart.update(); 
        }
      }, 0); 
    }

  getDisclaimerText(data){
    this.orderService.getBuCodeByAccountCode(data.mainAccountCode).subscribe((res: any) => {

      const loggedInUserBUs = JSON.parse(this.localstorageService.getItem(Constants.LOGGED_IN_USER_INFO))?.businessUnits;
      if(res.buCode && loggedInUserBUs && Array.isArray(loggedInUserBUs)){
        const selectedBuDetails = loggedInUserBUs.find(bu => bu.buCode === res.buCode)
        if(selectedBuDetails){
        this.disclaimerText = selectedBuDetails.disclaimerText ?? '';
        }
      }
    });
  }


  ngOnInit(): void {
    this.isLoading = true; 
    this.getDisclaimerText(this.dialogData);
    this.initializeChartData();
    this.isLoading = false;     
  }

  initializeChartData() {   
    if (!this.resultsToPlot || this.resultsToPlot.length === 0) { 
      return;
    }
  
    const chartData = this.resultsToPlot.map(result => {
      let dateValue: Date;
      
      if (result.collectionDate instanceof Date) {
        dateValue = result.collectionDate;
      } else {
        dateValue = new Date(result.collectionDate);
      }
      
      // Create a timezone-neutral date string to avoid timezone conversion issues
      const year = dateValue.getFullYear();
      const month = String(dateValue.getMonth() + 1).padStart(2, '0');
      const day = String(dateValue.getDate()).padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;
      
      return {
        x: dateString,
        y: result.resultValueWithoutUnit
      };
    });    
   
    
    this.lineChartData = [{
      label: this.dialogData.resultUnit || '',
      data: chartData,
      lineTension: 0,
      resultUnit: this.dialogData.resultUnit || '',
      backgroundColor: 'transparent',
      borderColor: '#07548c',
      pointBackgroundColor: '#07548c',
      pointBorderColor: '#07548c',
      pointStyle: 'rect',
      pointHoverBorderColor: '#07548c',
    }];
    
    const resultValue = this.resultsToPlot.map(result => result.resultValueWithoutUnit).filter(v => v != null);
    if (resultValue.length === 0) {
      return;
    }
    const maxValue = Math.max(...resultValue);
    const minValue = Math.min(...resultValue);
  
    const range = maxValue - minValue;
    const rawStepSize = range / 10; 
    const stepSize = Math.pow(10, Math.floor(Math.log10(rawStepSize))); 
    

    this.lineChartOptions = {
      ...this.lineChartOptions,
      scales: {
        ...this.lineChartOptions.scales,
        y: {
          ...this.lineChartOptions.scales.y,
          title: {
            ...this.lineChartOptions.scales.y.title,
            display: false,
            text: this.dialogData.resultUnit ? `Unit: ${this.dialogData.resultUnit}` : '',
          },
          ticks: {
            ...this.lineChartOptions.scales.y.ticks,
            stepSize: stepSize,
            max: maxValue,
          }
        }
      }
    };    
 
  }

  ngAfterViewInit() {  
    if (this.dialogHeader) {
      this.dialogHeader.nativeElement.focus();
      this.dialogContent.nativeElement.scrollTop = 0; 
    }
    
       setTimeout(() => {
      if (this.resultHistoryChart?.chart) {
        this.resultHistoryChart.chart.update();
      }
    }, 100);
  }

  toggleTrendline() {
    this.lineChartOptions = {
      ...this.lineChartOptions,
      plugins: {
        ...this.lineChartOptions.plugins,
        trendlinePlugin: {
          showTrendline: this.showTrendline
        }
      }
    };
    if (this.resultHistoryChart?.chart) {
      this.resultHistoryChart.chart.update(); 
    }
  }

  closeDialog() {
    this.dialogRef.close(false);
  }

  
  convertInputValueToPlot(value: string): number | string {
    if (!value) {
      return null;
    }
    value = value.replace(/,/g, '').trim();

    value = value.replace(/\s[a-zA-Z/%]+$/, '').trim();

    if (!isNaN(Number(value))) {
      return Number(value);
    }

    const numericRegex = /^[<>\s]*(\d+(\.\d+)?)/;
    const numericMatch = numericRegex.exec(value);
    if (numericMatch) {
      return parseFloat(numericMatch[1]);
    }

    return null;
  }

  async downloadAsPDF() {
    this.isLoading = true;

    await new Promise(resolve => setTimeout(resolve, 100));

    const tableScrollWrapper = this.dialogContent.nativeElement.querySelector('.table-scroll-wrapper');
    const originalHeight = tableScrollWrapper.style.maxHeight;
    tableScrollWrapper.style.maxHeight = 'none'; 
    const options = this.dialogContent.nativeElement.querySelector('.options');
    const dialogActions = this.dialogContent.nativeElement.querySelector('div[mat-dialog-actions]');
    const closeBtn = this.dialogContent.nativeElement.querySelector('.close-btn');
    options.style.display = 'none';
    dialogActions.style.display = 'none';
    closeBtn.style.display = 'none';
      
    html2canvas(this.dialogContent.nativeElement, {
      scrollY: -window.scrollY,
      useCORS: true,
      scale: 2 
    }).then(canvas => {
      const imgData = canvas.toDataURL('image/JPEG');
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const aspectRatio = imgWidth / imgHeight;
        
      const pdf = new jsPDF('l', 'mm', 'letter');
      const pageWidth = pdf.internal.pageSize.getWidth(); 
      const pageHeight = pdf.internal.pageSize.getHeight();
        
      const marginTop = 2; 
      const marginBottom = 2; 
      const marginHorizontal = 2; 
      const availableHeight = pageHeight - marginTop - marginBottom;
      const availableWidth = pageWidth - 2 * marginHorizontal;
  
      
      let pdfImgHeight = availableHeight;
      let pdfImgWidth = pdfImgHeight * aspectRatio;
        
      if (pdfImgWidth > availableWidth) {
        pdfImgWidth = availableWidth;
        pdfImgHeight = pdfImgWidth / aspectRatio;
      }
  
     
      const xOffset = (pageWidth - pdfImgWidth) / 2;
      const yOffset = marginTop;
  
     
      pdf.addImage(imgData, 'JPEG', xOffset, yOffset, pdfImgWidth, pdfImgHeight);
  
      let remainingHeight = imgHeight - (pdfImgHeight * (imgWidth / pdfImgWidth));
      let currentY = pdfImgHeight;

      while (remainingHeight > 1) {
        pdf.addPage();
          
        pdfImgHeight = availableHeight;
        pdfImgWidth = pdfImgHeight * aspectRatio;
  
        if (pdfImgWidth > availableWidth) {
          pdfImgWidth = availableWidth;
          pdfImgHeight = pdfImgWidth / aspectRatio;
        }
         
        pdf.addImage(imgData, 'JPEG', xOffset, yOffset, pdfImgWidth, pdfImgHeight, undefined, undefined, currentY);
        currentY += pdfImgHeight;
        remainingHeight -= pdfImgHeight * (imgWidth / pdfImgWidth);
      }
    
      tableScrollWrapper.style.maxHeight = originalHeight;
  
  
      options.style.display = '';
      dialogActions.style.display = '';
      closeBtn.style.display = '';
        
      pdf.save(`Graph Result for ${this.patientNameOnly}.pdf`);
      this.isLoading = false;
    }).catch(error => {
      console.error('Error generating PDF:', error);      
      tableScrollWrapper.style.maxHeight = originalHeight;
      options.style.display = '';
      dialogActions.style.display = '';
      closeBtn.style.display = '';
      this.isLoading = false;
    });
}
  
}
